<script lang="ts" setup>
import { MediaImageFragment } from '~/api/graphql/generated'
import { Link } from '~/types'

export interface Employee {
  id?: number
  name: string
  email?: string
  phone?: string
  position: string
  description?: string
  link?: Link
  image: MediaImageFragment
}
const props = defineProps<{ data: Employee }>()

const config = useRuntimeConfig()
const { t } = useI18n()

const downloadUrl = `${config.public.drupalApiUrl}/employee/${props.data.id}/vcard`
const email = ref(props.data.email)
const isCopying = ref(false)
const profile = computed(() => ({
  url: `${config.public.frontendBaseUrl}${props.data.link?.url}`,
  text: isCopying.value ? t('employee.detail.copiedUrlText') : t('employee.detail.copyUrlText'),
}))

const handleCopyClick = () => {
  isCopying.value = true
  navigator.clipboard.writeText(profile.value.url)
  setTimeout(() => {
    isCopying.value = false
  }, 2000)
}

const controller = useModal()
</script>
<template>
  <article class="container grid">
    <Image class="t-4" type="employee" :entity="data.image" :alt="data.name" />
    <div class="info t-8 tl-7 tl-o-1">
      <div v-if="data.name">
        <h2 class="name">{{ data.name }}</h2>
        <p v-if="data.position" class="position" v-html="data.position" />
      </div>
      <ul v-if="data.email || data.phone" class="contact cul">
        <li v-if="data.email">
          <h5>{{ t('employee.detail.email') }}</h5>
          <button v-if="email" class="link-default text-small" @click="controller.open">
            {{ t('form.emailContactForm.employeeSendEmailLabel') }}
          </button>
          <ModalContactForm v-if="email" :controller="controller">
            <EmailContactForm :email-to="email" :recipient-name="data.name" />
          </ModalContactForm>
        </li>
        <li v-if="data.phone">
          <h5>{{ t('employee.detail.phone') }}</h5>
          <a class="link-default text-small" :href="`tel:${data.phone}`"> {{ data.phone }} </a>
        </li>
      </ul>
      <div v-if="data.description">
        <p class="description text-small" v-html="data.description" />
      </div>
      <div class="cta">
        <Button secondary icon="download" :href="downloadUrl" :mime-type="`text/vcard`">
          {{ t('employee.detail.vcard') }}
        </Button>
        <TextLink icon="link-alt" @click="handleCopyClick"> {{ profile.text }} </TextLink>
      </div>
    </div>
  </article>
</template>
<style lang="scss" scoped>
.dp-image {
  --image-size: 300px;

  width: 100%;
  max-width: var(--image-size);
  aspect-ratio: 1;

  object-fit: cover;

  margin-bottom: 32px;

  @include breakpoint(tl) {
    --image-size: 400px;
  }
}

.info {
  display: grid;
  grid-template-columns: 100%;
  gap: 48px;
  @include breakpoint(tl) {
    margin-top: 32px;
  }
}

.contact {
  display: grid;
  grid-template-columns: 100%;
  gap: 16px;
  li {
    display: grid;
    grid-template-columns: 60px 1fr;
    gap: var(--gap);
    justify-items: start;
  }
  h5 {
    margin-bottom: 0;
  }
}

.name {
  margin-bottom: 4px;
}

.position,
.description {
  margin-bottom: 0;
}

.cta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  @include breakpoint(t) {
    flex-direction: row;
    align-items: center;
  }
}
</style>
